import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import usePageMetadata from '../utils/usePageMetadata';
import './Alerts.css';

// Available metrics from all dashboards (Analytics, Revenue, Content)
const AVAILABLE_METRICS = [
  // Core metrics
  { key: 'users', label: 'Total Users', category: 'Core' },
  { key: 'sessions', label: 'Sessions', category: 'Core' },
  { key: 'pageviews', label: 'Pageviews', category: 'Core' },
  { key: 'revenue', label: 'Total Revenue', category: 'Core' },
  { key: 'engagementRate', label: 'Engagement Rate', category: 'Core' },
  { key: 'bounceRate', label: 'Bounce Rate', category: 'Core' },
  { key: 'adImpressions', label: 'Ad Impressions', category: 'Core' },
  
  // User metrics
  { key: 'newUsers', label: 'New Users', category: 'User Metrics' },
  { key: 'returnUsers', label: 'Returning Users', category: 'User Metrics' },
  { key: 'returningUsersPercentage', label: '% Returning Visitors', category: 'User Metrics' },
  { key: 'usersPerSession', label: 'Users/Session', category: 'User Metrics' },
  
  // Engagement metrics
  { key: 'engagedSessionsPerUser', label: 'Engaged Sessions/User', category: 'Engagement' },
  { key: 'engagementTime', label: 'Avg Engagement Time', category: 'Engagement' },
  { key: 'pageviewsPerUser', label: 'Views/User', category: 'Engagement' },
  
  // Revenue metrics
  { key: 'rpu', label: 'RPU (Revenue Per User)', category: 'Revenue' },
  { key: 'rpm', label: 'RPM (Revenue Per Mille)', category: 'Revenue' },
  { key: 'cpm', label: 'CPM (Cost Per Mille)', category: 'Revenue' },
  { key: 'viewability', label: 'Viewability Rate', category: 'Revenue' },
];

const TIME_FRAMES = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

const DIRECTIONS = [
  { value: 'increase', label: 'Increase' },
  { value: 'decrease', label: 'Decrease' },
  { value: 'either', label: 'Either' },
];

const Alerts = () => {
  usePageMetadata({
    title: 'Alerts | The Ad Shop',
    description: 'Set up automated alerts to monitor your site performance metrics and get notified of significant changes.'
  });

  const { token, user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [sites, setSites] = useState([]);

  const isPaidPlan = user?.plan && user.plan !== 'free';

  // Fetch sites
  const fetchSites = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get('/api/sites', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sitesData = response.data.sites || [];
      setSites(sitesData);
      
      // Set default site if available
      if (sitesData.length > 0 && !selectedSiteId) {
        setSelectedSiteId(sitesData[0].siteId);
        setNewAlert(prev => ({ ...prev, siteId: sitesData[0].siteId }));
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  }, [token, selectedSiteId]);

  const [newAlert, setNewAlert] = useState({
    siteId: '',
    metric: '',
    metricLabel: '',
    timeFrame: 'week',
    percentageChange: 10,
    direction: 'decrease',
    enabled: true
  });

  const fetchAlerts = useCallback(async () => {
    if (!isPaidPlan) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/alerts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.alerts) {
        setAlerts(response.data.alerts);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
      if (error.response?.status !== 404) {
        setError('Failed to load alerts');
      }
    } finally {
      setLoading(false);
    }
  }, [token, isPaidPlan]);

  useEffect(() => {
    if (token && isPaidPlan) {
      fetchSites();
      fetchAlerts();
    } else {
      setLoading(false);
    }
  }, [token, isPaidPlan, fetchSites, fetchAlerts]);

  const handleMetricChange = (metricKey) => {
    const metric = AVAILABLE_METRICS.find(m => m.key === metricKey);
    setNewAlert({
      ...newAlert,
      metric: metricKey,
      metricLabel: metric ? metric.label : ''
    });
  };

  const isDemoUser = user?.email === 'demo@example.com';

  const handleCreateAlert = async () => {
    if (isDemoUser) return; // Demo users can't create alerts
    if (!newAlert.siteId || !newAlert.metric || !newAlert.metricLabel) {
      setError('Please fill in all required fields');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axios.post('/api/alerts', newAlert, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Alert created successfully!');
      setShowAddForm(false);
      setNewAlert({
        siteId: selectedSiteId || (sites[0]?.siteId || ''),
        metric: '',
        metricLabel: '',
        timeFrame: 'week',
        percentageChange: 10,
        direction: 'decrease',
        enabled: true
      });
      setTimeout(() => setSuccess(''), 3000);
      await fetchAlerts();
    } catch (error) {
      console.error('Error creating alert:', error);
      setError(error.response?.data?.message || 'Failed to create alert');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAlert = async (alertId, enabled) => {
    if (isDemoUser) return; // Demo users can't toggle alerts
    setSaving(true);
    setError('');
    try {
      await axios.put('/api/alerts', { id: alertId, enabled: !enabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchAlerts();
    } catch (error) {
      console.error('Error toggling alert:', error);
      setError('Failed to update alert');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (alertId) => {
    if (isDemoUser) return; // Demo users can't delete alerts
    if (!window.confirm('Are you sure you want to delete this alert?')) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      await axios.delete(`/api/alerts?id=${alertId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchAlerts();
      setSuccess('Alert deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting alert:', error);
      setError('Failed to delete alert');
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return <div className="alerts-page">Please log in to view alerts.</div>;
  }

  if (!isPaidPlan) {
    return (
      <div className="alerts-page">
        <div className="alerts-container">
          <div className="alerts-header">
            <h1>Email Alerts</h1>
            <p className="alerts-subtitle">
              Get notified when important metrics change
            </p>
          </div>
          <div className="upgrade-prompt">
            <div className="upgrade-icon">🔔</div>
            <h2>Upgrade to Enable Email Alerts</h2>
            <p>
              Email alerts are available for paid plans. Set up automated notifications 
              when your metrics change beyond your thresholds.
            </p>
            <Link to="/billing" className="upgrade-button">
              Upgrade Plan
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const groupedMetrics = AVAILABLE_METRICS.reduce((acc, metric) => {
    if (!acc[metric.category]) {
      acc[metric.category] = [];
    }
    acc[metric.category].push(metric);
    return acc;
  }, {});

  return (
    <div className="alerts-page">
      <div className="alerts-container">
        <div className="alerts-header">
          <h1>Email Alerts</h1>
          <p className="alerts-subtitle">
            Configure automated email alerts for metric changes
          </p>
        </div>

        {error && (
          <div className="alert-message error">
            {error}
          </div>
        )}

        {success && (
          <div className="alert-message success">
            {success}
          </div>
        )}

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading alerts...</p>
          </div>
        ) : (
          <>
            {!isDemoUser && (
              <div className="alerts-actions">
                <button 
                  className="add-alert-button"
                  onClick={() => setShowAddForm(!showAddForm)}
                >
                  {showAddForm ? 'Cancel' : '+ Add New Alert'}
                </button>
              </div>
            )}

            {showAddForm && (
              <div className="alert-form-card">
                <h2>Create New Alert</h2>
                <div className="alert-form">
                  <div className="form-group">
                    <label>Site</label>
                    <select
                      value={newAlert.siteId}
                      onChange={(e) => {
                        const siteId = e.target.value;
                        setSelectedSiteId(siteId);
                        setNewAlert({ ...newAlert, siteId });
                      }}
                    >
                      <option value="">Select a site</option>
                      {sites.map(site => (
                        <option key={site.siteId} value={site.siteId}>
                          {site.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Metric</label>
                    <select
                      value={newAlert.metric}
                      onChange={(e) => handleMetricChange(e.target.value)}
                    >
                      <option value="">Select a metric</option>
                      {Object.entries(groupedMetrics).map(([category, metrics]) => (
                        <optgroup key={category} label={category}>
                          {metrics.map(metric => (
                            <option key={metric.key} value={metric.key}>
                              {metric.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Time Frame</label>
                      <select
                        value={newAlert.timeFrame}
                        onChange={(e) => setNewAlert({ ...newAlert, timeFrame: e.target.value })}
                      >
                        {TIME_FRAMES.map(tf => (
                          <option key={tf.value} value={tf.value}>
                            {tf.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Percentage Change (%)</label>
                      <input
                        type="number"
                        min="0.1"
                        max="1000"
                        step="0.1"
                        value={newAlert.percentageChange}
                        onChange={(e) => setNewAlert({ ...newAlert, percentageChange: parseFloat(e.target.value) || 0 })}
                      />
                    </div>

                    <div className="form-group">
                      <label>Direction</label>
                      <select
                        value={newAlert.direction}
                        onChange={(e) => setNewAlert({ ...newAlert, direction: e.target.value })}
                      >
                        {DIRECTIONS.map(dir => (
                          <option key={dir.value} value={dir.value}>
                            {dir.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <button 
                    className="save-button"
                    onClick={handleCreateAlert}
                    disabled={saving || !newAlert.siteId || !newAlert.metric}
                  >
                    {saving ? 'Creating...' : 'Create Alert'}
                  </button>
                </div>
              </div>
            )}

            {alerts.length > 0 ? (
              <div className="alerts-list">
                <h2>Your Alerts ({alerts.length})</h2>
                {alerts.map((alert) => {
                  const site = sites.find(s => s.siteId === alert.siteId);
                  return (
                    <div key={alert._id || alert.id} className="alert-item">
                      <div className="alert-info">
                        <div className="alert-header-row">
                          <h3>{alert.metricLabel}</h3>
                          {!isDemoUser ? (
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={alert.enabled}
                                onChange={() => handleToggleAlert(alert._id || alert.id, alert.enabled)}
                                disabled={saving}
                              />
                              <span className="toggle-slider"></span>
                              <span className="toggle-label">{alert.enabled ? 'Enabled' : 'Disabled'}</span>
                            </label>
                          ) : (
                            <label className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={alert.enabled}
                                disabled={true}
                                readOnly
                              />
                              <span className="toggle-slider"></span>
                              <span className="toggle-label">{alert.enabled ? 'Enabled' : 'Disabled'}</span>
                            </label>
                          )}
                        </div>
                        <div className="alert-details">
                          <p><strong>Site:</strong> {site?.name || alert.siteId}</p>
                          <p><strong>Time Frame:</strong> {TIME_FRAMES.find(tf => tf.value === alert.timeFrame)?.label || alert.timeFrame}</p>
                          <p><strong>Threshold:</strong> {alert.percentageChange}% {DIRECTIONS.find(d => d.value === alert.direction)?.label.toLowerCase()}</p>
                        </div>
                      </div>
                      {!isDemoUser && (
                        <button 
                          className="delete-button"
                          onClick={() => handleDelete(alert._id || alert.id)}
                          disabled={saving}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              !showAddForm && (
                <div className="empty-state">
                  <p>No alerts configured yet. Create your first alert to get started!</p>
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Alerts;
